<template>
  <div class="relative" :class="{ 'mt-2': segmentIndex > 0 }">
    <div
      class="bg-background-neutral-secondary grid grid-cols-[1fr_auto_1fr_auto_1fr_auto] items-stretch rounded-2xl px-3 py-2"
    >
      <SegmentCell
        :primary="dep?.code ? depCityName || dep.code : ''"
        :secondary="dep?.code ?? ''"
        :placeholder="$t('main.cities.departure')"
        :has-error="!!validation?.dep?.$error"
        @click="emit('onFromInputClick')"
      />

      <button
        type="button"
        class="flex shrink-0 items-center justify-center self-center rounded-full p-1 active:shadow-md"
        :aria-label="$t('main.multiTrip.swapDirections')"
        @click="emit('onSwap')"
      >
        <!-- prettier-ignore -->
        <svg
          class="text-text-neutral-quaternary rotate-90"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          fill="none"
          aria-hidden="true"
        ><path fill="currentColor" fill-rule="evenodd" d="M20.158 14.204a1 1 0 0 1-.002 1.414l-4.7 4.69a1 1 0 0 1-1.706-.708V4.4a1 1 0 1 1 2 0v12.79l2.994-2.988a1 1 0 0 1 1.414.002ZM9.633 3.476a1 1 0 0 1 .617.924v15.2a1 1 0 1 1-2 0V6.814L5.257 9.807a1 1 0 1 1-1.414-1.414l4.7-4.7a1 1 0 0 1 1.09-.217Z" clip-rule="evenodd"/></svg>
      </button>

      <SegmentCell
        :primary="arr?.code ? arrCityName || arr.code : ''"
        :secondary="arr?.code ?? ''"
        :placeholder="$t('main.cities.arrival')"
        :has-error="!!validation?.arr?.$error"
        @click="emit('onToInputClick')"
      />

      <span
        class="bg-background-neutral-tertiary mx-2 my-2 w-0.5 shrink-0 self-stretch rounded-full opacity-50"
        aria-hidden="true"
      />

      <SegmentCell
        :primary="primaryDateText"
        :secondary="secondaryDateText"
        :placeholder="$t('main.multiTrip.datePlaceholder')"
        @click="
          emit('onDateSelect', searchFormCalendar.config.CALENDAR_MODES.SINGLE)
        "
      />
    </div>

    <button
      v-if="segmentIndex === 0"
      type="button"
      class="bg-background-positive-primary absolute -right-2 -top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full"
      :aria-label="$t('main.multiTrip.info.ariaLabel')"
      @click="emit('onOpenInfo')"
    >
      <span class="text-static-white inline-flex h-6 w-6" aria-hidden="true">
        <QuestionIcon class="h-6 w-6" />
      </span>
    </button>

    <FreeCloseButton
      v-if="isRemovable"
      class="absolute -right-2 -top-2 z-10"
      :aria-label="$t('main.multiTrip.removeLeg')"
      @click="emit('onRemove')"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { FreeCloseButton } from 'freedom-shared';
  import {
    dateUtils,
    searchForm,
    searchFormCalendar,
    type MappedLocation,
  } from 'shared';

  import QuestionIcon from '@/assets/images/multi-trip/question-icon-24x24.svg?component';

  import SegmentCell from './MultiTripSegmentCell.vue';

  type SegmentValidation = {
    dep?: { $error?: boolean };
    arr?: { $error?: boolean };
  };

  const props = defineProps<{
    dep?: MappedLocation;
    arr?: MappedLocation;
    dates?: string[];
    segmentIndex: number;
    source: string;
    validation?: SegmentValidation;
  }>();

  const isRemovable = computed(() => props.segmentIndex > 0);

  const emit = defineEmits<{
    onFromInputClick: [];
    onToInputClick: [];
    onSwap: [];
    onDateSelect: [mode: searchFormCalendar.config.CalendarMode];
    onOpenInfo: [];
    onRemove: [];
  }>();

  const formattedCities = computed(() =>
    searchForm.lib.formatSearchCities(props.dep, props.arr),
  );
  const depCityName = computed(() => formattedCities.value.dep.value);
  const arrCityName = computed(() => formattedCities.value.arr.value);

  const primaryDateText = computed(() => {
    const departureDate = props.dates?.[0];
    return departureDate ? dateUtils.getVerbalDate(departureDate) : '';
  });
  // Multi-trip rows show the full weekday name (e.g. “пятница”) instead of the
  // 2-char abbreviation used in the simple flow.
  const secondaryDateText = computed(() => {
    const departureDate = props.dates?.[0];
    return departureDate ? dateUtils.format(departureDate, 'dddd') : '';
  });
</script>
